import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Checkbox,
    FormControlLabel,
    Button,
    useTheme,
    Grid,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useCreateLevel } from '../../hooks/levels/useCreateLevel';
import { useUpdateLevel } from '../../hooks/levels/useUpdateLevel';
import { useGetAcademicTerms } from '../../hooks/academicTerms/useGetAcademicTerms';
import { useCurrentContext } from '../../hooks/useCurrentContext';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const FormAddEditLevel = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';
    const { enqueueSnackbar } = useSnackbar();

    const { createLevel, isPending: isCreating } = useCreateLevel();
    const { editLevel, isUpdate } = useUpdateLevel();
    const { isLoading: termsLoading, academicTerms } = useGetAcademicTerms(1, 999);
    const { currentTermId } = useCurrentContext();

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        academic_term_id: '',
        code: '',
        name: '',
        age_from: '',
        age_to: '',
        is_active: true,
    });

    // Auto-set current term for new records
    useEffect(() => {
        if (!isEdit && currentTermId && !form.academic_term_id) {
            setForm((p) => ({ ...p, academic_term_id: currentTermId }));
        }
    }, [currentTermId, isEdit]);

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            academic_term_id: currentData.academic_term_id || '',
            code: currentData.code || '',
            name: currentData.name || '',
            age_from: currentData.age_from ?? '',
            age_to: currentData.age_to ?? '',
            is_active: currentData.is_active ?? true,
        });
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setForm((prev) => ({ ...prev, [name]: checked }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleTermChange = (e) => {
        setForm((prev) => ({ ...prev, academic_term_id: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const ageFrom = Number(form.age_from);
        const ageTo = Number(form.age_to);

        if (isNaN(ageFrom) || ageFrom < 0) {
            enqueueSnackbar(
                isArabic ? 'العمر من يجب أن يكون رقم صحيح 0 أو أكبر' : 'Age From must be 0 or greater',
                { variant: 'error' }
            );
            return;
        }

        if (isNaN(ageTo) || ageTo < 0) {
            enqueueSnackbar(
                isArabic ? 'العمر إلى يجب أن يكون رقم صحيح 0 أو أكبر' : 'Age To must be 0 or greater',
                { variant: 'error' }
            );
            return;
        }

        if (ageTo < ageFrom) {
            enqueueSnackbar(
                isArabic ? '"العمر إلى" يجب أن يكون أكبر من أو يساوي "العمر من"' : '"Age To" must be greater than or equal to "Age From"',
                { variant: 'error' }
            );
            return;
        }

        const payload = {
            academic_term_id: Number(form.academic_term_id),
            code: form.code || null,
            name: form.name,
            age_from: ageFrom,
            age_to: ageTo,
            is_active: !!form.is_active,
        };

        if (isEdit && currentData?.id) {
            editLevel({ req: payload, id: currentData.id });
        } else {
            createLevel(payload);
        }
    };

    if (termsLoading) return <LoadingPage />;

    const termOptions = (academicTerms || []).map((t) => ({ id: t.id, name: t.name }));

    const ageFrom = Number(form.age_from);
    const ageTo = Number(form.age_to);
    const ageError = form.age_from !== '' && form.age_to !== '' && !isNaN(ageTo) && !isNaN(ageFrom) && ageTo < ageFrom;

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? 'rtl' : 'ltr',
                backgroundColor: isDark && 'transparent',
            }}
        >
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'الفصل الدراسي' : 'Academic Term'}
                            type="select"
                            name="academic_term_id"
                            value={form.academic_term_id}
                            onChange={handleTermChange}
                            options={termOptions}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'كود المستوى' : 'Level Code'}
                            placeholder={isArabic ? 'مثال: L1' : 'e.g. L1'}
                            type="text"
                            name="code"
                            value={form.code}
                            onChange={handleChange}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'اسم المستوى' : 'Level Name'}
                            placeholder={isArabic ? 'ادخل اسم المستوى' : 'Enter level name'}
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormInput
                            label={isArabic ? 'العمر من' : 'Age From'}
                            placeholder="0"
                            type="number"
                            name="age_from"
                            value={form.age_from}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormInput
                            label={isArabic ? 'العمر إلى' : 'Age To'}
                            placeholder="0"
                            type="number"
                            name="age_to"
                            value={form.age_to}
                            onChange={handleChange}
                            error={ageError ? (isArabic ? '"العمر إلى" يجب أن يكون >= "العمر من"' : '"Age To" must be >= "Age From"') : undefined}
                        />
                        {ageError && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {isArabic
                                    ? '"العمر إلى" يجب أن يكون أكبر من أو يساوي "العمر من"'
                                    : '"Age To" must be greater than or equal to "Age From"'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 1, md: 1 } }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.is_active}
                                        name="is_active"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                }
                                label={isArabic ? 'مفعّل' : 'Active'}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit || ageError}
                    >
                        {loadingSubmit
                            ? isArabic ? 'جاري الحفظ...' : 'Saving...'
                            : isEdit
                                ? isArabic ? 'تحديث' : 'Update'
                                : isArabic ? 'حفظ' : 'Save'}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditLevel;
